import React, { createContext, useContext, useState, ReactNode } from 'react'
import { transactionService } from '../../../../../../services/transactionService'
import { listingService } from '../../../../../../services/listingService'
import type { Transaction, Listing } from '@popety-io/types'
import { buildingPermitsService } from '@/pages/Explore/DeveloperMap/services'
import { BuildingPermit } from '@/pages/Explore/BuildingPermits/types'

interface HistoryContextState {
  transactions: Transaction[]
  listings: Listing[]
  buildingPermits: BuildingPermit[]
  loading: boolean
  error: string | null
  fetchHistoryData: (
    transactionIds: string[],
    listingIds: string[],
    buildingPermitIds: string[]
  ) => Promise<void>
}

const HistoryContext = createContext<HistoryContextState | undefined>(undefined)

export const useHistoryContext = () => {
  const context = useContext(HistoryContext)
  if (!context) {
    throw new Error('useHistoryContext must be used within a HistoryProvider')
  }
  return context
}

interface HistoryProviderProps {
  children: ReactNode
}

export const HistoryProvider: React.FC<HistoryProviderProps> = ({
  children,
}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [listings, setListings] = useState<Listing[]>([])
  const [buildingPermits, setBuildingPermits] = useState<BuildingPermit[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchHistoryData = async (
    transactionIds: string[],
    listingIds: string[],
    buildingPermitIds: string[]
  ) => {
    setLoading(true)
    setError(null)

    try {
      const promises = []

      if (buildingPermitIds.length > 0) {
        promises.push(
          buildingPermitsService
            .getBulk(buildingPermitIds as any)
            .then(setBuildingPermits)
            .catch((err) => {
              console.error('Error fetching building permits:', err)
              setError('Failed to fetch building permits')
            })
        )
      }

      // Fetch transactions if IDs are provided
      if (transactionIds.length > 0) {
        promises.push(
          transactionService
            .getTransactionsForHistory(transactionIds)
            .then(setTransactions)
            .catch((err) => {
              console.error('Error fetching transactions:', err)
              setTransactions([])
            })
        )
      } else {
        setTransactions([])
      }

      // Fetch listings if IDs are provided
      if (listingIds.length > 0) {
        promises.push(
          listingService
            .getListingsForHistory(listingIds)
            .then(setListings)
            .catch((err) => {
              console.error('Error fetching listings:', err)
              setListings([])
            })
        )
      } else {
        setListings([])
      }

      // Wait for all requests to complete
      await Promise.all(promises)
    } catch (err) {
      console.error('Error in fetchHistoryData:', err)
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred while fetching history data'
      )
    } finally {
      setLoading(false)
    }
  }

  const contextValue: HistoryContextState = {
    transactions,
    listings,
    buildingPermits,
    loading,
    error,
    fetchHistoryData,
  }

  return (
    <HistoryContext.Provider value={contextValue}>
      {children}
    </HistoryContext.Provider>
  )
}
